import React, { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Bike, LockKeyhole } from "lucide-react";

const BG_IMG =
  "https://images.pexels.com/photos/1029622/pexels-photo-1029622.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Login() {
  const { user, login, error } = useAuth();
  const [username, setUsername] = useState("admin");
  const [password, setPassword] = useState("admin");
  const [submitting, setSubmitting] = useState(false);
  const loc = useLocation();
  const nav = useNavigate();

  if (user) return <Navigate to={loc.state?.from?.pathname || "/"} replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const ok = await login(username, password);
    setSubmitting(false);
    if (ok) nav("/");
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
      <div
        className="hidden lg:flex relative bg-cover bg-center"
        style={{ backgroundImage: `url(${BG_IMG})` }}
        data-testid="login-art"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-white/60 via-white/30 to-transparent" />
        <div className="relative z-10 p-10 flex flex-col justify-between w-full">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-slate-900 flex items-center justify-center">
              <Bike className="w-4 h-4 text-white" />
            </div>
            <span className="text-sm font-bold tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>
              hoomu · bridge · evolvo
            </span>
          </div>
          <div className="max-w-md">
            <h2 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Chivo, sans-serif" }}>
              Seguros a demanda para cada alquiler.
            </h2>
            <p className="mt-2 text-sm text-slate-700 leading-relaxed">
              Middleware entre Evolvo ERP e InsuranceSoft. Idempotencia por alquiler,
              cola de reintentos, trazabilidad de cada póliza.
            </p>
            <div className="mt-6 grid grid-cols-3 gap-3 text-[10px] font-mono uppercase tracking-wider text-slate-600">
              <div className="p-2 border border-slate-300/60 rounded-md bg-white/40 backdrop-blur-sm">idempotente</div>
              <div className="p-2 border border-slate-300/60 rounded-md bg-white/40 backdrop-blur-sm">auditado</div>
              <div className="p-2 border border-slate-300/60 rounded-md bg-white/40 backdrop-blur-sm">reintenable</div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-10">
        <form onSubmit={onSubmit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div className="space-y-1">
            <div className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-slate-500">
              <LockKeyhole className="w-3 h-3" /> Acceso administrador
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Chivo, sans-serif" }}>
              Iniciar sesión
            </h1>
            <p className="text-xs text-slate-500">
              Credenciales por defecto: <code className="font-mono">admin</code> / <code className="font-mono">admin</code>
            </p>
          </div>

          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="username" className="text-xs font-semibold">Usuario</Label>
              <Input
                id="username"
                data-testid="login-username-input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoFocus
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs font-semibold">Contraseña</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
          </div>

          {error && (
            <div
              data-testid="login-error"
              className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2"
            >
              {error}
            </div>
          )}

          <Button
            type="submit"
            data-testid="login-submit-button"
            disabled={submitting}
            className="w-full bg-slate-900 hover:bg-slate-800 text-white h-10 font-semibold tracking-wide"
          >
            {submitting ? "Entrando…" : "Entrar"}
          </Button>

          <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400 text-center">
            hoomu · bridge · v1.0
          </p>
        </form>
      </div>
    </div>
  );
}
